// pages/WebsitePhotosPage.js — photos for the website gallery.  [marker: website-feed v1]
// The website shows them as a swipeable slideshow in random order, each captioned with its event.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function WebsitePhotosPage() {
  const { isManagerOrAbove } = useAuth();
  const canEdit = isManagerOrAbove();
  const [photos, setPhotos] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/website/photos').then(r => setPhotos(r.data.photos)).catch(() => { setPhotos([]); toast.error('Could not load the photos.'); });
  useEffect(() => {
    load();
    api.get('/events?limit=200').then(r => setEvents(Array.isArray(r.data) ? r.data : (r.data.data || r.data.events || []))).catch(() => {});
  }, []);
  const upload = async (files) => {
    if (!files || !files.length) return;
    if (!eventId && !caption.trim()) return toast.error('First pick the event the photos are from, or type a caption.');
    setBusy(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('files', f));
      if (eventId) fd.append('eventId', eventId);
      if (caption.trim()) fd.append('caption', caption.trim());
      const r = await api.post('/website/photos', fd, { headers: { 'Content-Type': undefined } });
      toast.success(`${r.data.added} photo${r.data.added === 1 ? '' : 's'} added to the website gallery.`); load();
    } catch (err) { toast.error(err.response?.data?.error || 'The photos could not be uploaded.'); }
    finally { setBusy(false); }
  };
  const patch = async (p, body, msg) => {
    try { await api.patch(`/website/photos/${p.id}`, body); if (msg) toast.success(msg); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
  };
  const del = async (p) => {
    if (!window.confirm('Delete this photo from the website gallery?')) return;
    try { await api.delete(`/website/photos/${p.id}`); toast.success('Photo deleted.'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
  };
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Website Photos</h1>
          <p className="page-subtitle">The gallery on njjba.com: a swipeable slideshow, shown in random order, with each photo's event underneath.</p>
        </div>
      </div>
      {canEdit && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Add photos</span></div>
          <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ minWidth: 240, flex: 1, margin: 0 }}>
              <label className="form-label" htmlFor="wp-event">Event the photos are from</label>
              <select id="wp-event" className="form-select" value={eventId} onChange={e => setEventId(e.target.value)}>
                <option value="">Choose an event…</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}{ev.event_date ? ` (${String(ev.event_date).slice(0, 4)})` : ''}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ minWidth: 240, flex: 1, margin: 0 }}>
              <label className="form-label" htmlFor="wp-caption">Or a caption</label>
              <input id="wp-caption" className="form-input" value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Day of Advocacy in Washington, 2026" />
            </div>
            <label className="btn btn-primary" style={{ cursor: busy ? 'wait' : 'pointer' }}>
              {busy ? 'Uploading…' : 'Choose photos…'}
              <input type="file" multiple accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} disabled={busy} onChange={e => { upload(e.target.files); e.target.value = ''; }} />
            </label>
          </div>
        </div>
      )}
      {photos === null ? <p className="text-muted">Loading…</p> : photos.length === 0 ? (
        <div className="empty-state" style={{ padding: 30 }}><p>No photos yet. Pick an event above and choose photos to start the gallery.</p></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14 }}>
          {photos.map(p => (
            <div key={p.id} className="card" style={{ overflow: 'hidden', opacity: p.is_visible ? 1 : 0.55 }}>
              <img alt="" src={p.url} style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }} loading="lazy" />
              <div style={{ padding: 10, display: 'grid', gap: 6 }}>
                <div style={{ fontSize: '0.82rem' }}>{p.caption || p.event_name || <span className="text-muted">No caption</span>}</div>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => { const c = window.prompt('Caption for this photo', p.caption || p.event_name || ''); if (c !== null) patch(p, { caption: c }, 'Caption saved.'); }}>Edit caption</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => patch(p, { isVisible: !p.is_visible }, p.is_visible ? 'Hidden from the website.' : 'Shown on the website.')}>{p.is_visible ? 'Hide' : 'Show'}</button>
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => del(p)}>Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
